/** Turning an observation plus a pointer target into words.
 *
 * Every string a player reads outside a content def is produced here, in pure
 * functions the test can pin. The one thing this module never writes is the
 * text of an illegality: that comes from the kernel's own `reasonText`, so the
 * wording of "not enough gold" lives in exactly one place and cannot drift
 * between the status bar, a tooltip and the log.
 *
 * ## What the plate is allowed to say
 *
 * The plate is player-facing, so it names the *thing* under the pointer and
 * what visiting it would do - "Windmill", "Sawmill - Produces 2 wood a day",
 * "Skyclaw Aerie - Guarded" - and nothing else. It printed
 * `Grass - (17, 40, level 1)` for two rounds: raw tile coordinates and the
 * engine's own z index, rendered as prose to a player who has no use for
 * either. Coordinates are a developer's tool and live behind
 * `cellDebugText()`, which the shipping plate never calls (see `StatusBar`).
 *
 * The other rule is that no `kind` from the content vocabulary reaches the
 * screen. "bonus", "bank", "dwelling" and "portal" are how the kernel
 * dispatches a visit; a player is owed the *consequence*, which is what
 * `objectEffect` builds out of the def's payload.
 */

import { RESOURCES, reasonText } from "@game/core";
import type {
  ContentRegistry, CreatureDefId, MapObjectDef, ObservedObject, PlayerObservation,
  SpellDefId, TerrainKind,
} from "@game/core";
import { formatNumber, humanise } from "./format.js";
import { colourFor } from "./minimap-paint.js";
import { findHero, findObject, findTown, magicGroundAt, terrainAt } from "./select.js";
import { heroDisplayName } from "./hero-name.js";
import type { HoverTarget, StatusState } from "./types.js";

export interface HoverDescription {
  readonly title: string;
  readonly detail: readonly string[];
}

// --------------------------------------------------------------- object names

/**
 * `MapObjectDef` still has no `name` field (STATUS.md, "Types the next agent
 * will want"), so a name has to be recovered from the id, which is the atlas
 * key. The keys stack a vendor prefix and then a family prefix - `expansion-`,
 * `bld_`, `mine_`, `dwl_`, `res_`, `pu_`, `tp_` - and `humanise` only knows
 * four of them, which is why the plate could say "Res Wood" and "Dwl Grove".
 *
 * Vendor prefixes strip repeatedly; the family prefix strips exactly once.
 * `town_` and `hero_` are not families here: those ids are reached through the
 * TOWN and HERO hovers, which carry a real name, and stripping them would turn
 * `tp_town_gate` into "Gate". A trailing `_obj` is a disambiguator against an
 * artifact of the same name (`dwl_ancient_lamp`) and is not part of it.
 */
const VENDOR_PREFIX = /^(?:obj)[._]/;
const FAMILY_PREFIX = /^(?:bld|bank|mine|dwl|mon|res|pu|tp|obs|boat)[._]/;

/** Words that name a resource rather than a place, so `mine_gold` is a mine
 *  and not a nugget. `mine_sawmill` and `mine_stone_quarry` already name a
 *  place and are left alone. */
const BARE_RESOURCE = new Set<string>([...RESOURCES, "gem"]);

export function objectDisplayName(
  object: ObservedObject, def: MapObjectDef | undefined, content: ContentRegistry | null,
): string {
  // A wandering stack is not a building with a name: it is the creatures, and
  // The classic names it after them.
  if (object.kind === "monster") {
    const creature = object.creature
      ?? (def === undefined ? null : readString(def.payload, "creature"));
    const cd = creature === null ? undefined : content?.creatures.get(creature as CreatureDefId);
    if (cd !== undefined) return cd.namePlural;
  }

  let stripped = object.def;
  for (;;) {
    const next = stripped.replace(VENDOR_PREFIX, "");
    if (next === stripped) break;
    stripped = next;
  }
  stripped = stripped.replace(FAMILY_PREFIX, "").replace(/[._]obj$/, "");
  const name = humanise(stripped);
  if (object.kind === "mine" && BARE_RESOURCE.has(stripped)) return `${name} Mine`;
  return name;
}

// ------------------------------------------------------------- object effects

type Payload = MapObjectDef["payload"];

function readNumber(p: Payload, key: string): number {
  const v = p[key];
  return typeof v === "number" ? v : 0;
}

function readString(p: Payload, key: string): string | null {
  const v = p[key];
  return typeof v === "string" && v !== "" ? v : null;
}

const isTrue = (p: Payload, key: string): boolean => p[key] === true;

/** `gems` is the only resource id that is already a plural, and "1 gems a day"
 *  is the kind of seam a player reads as a bug in the game rather than in the
 *  string. Everything else reads the same at any count. */
const resourceWord = (res: string, n: number): string =>
  res === "gems" && n === 1 ? "gem" : res;

/** "a", "a and b", "a, b and c" - the plate is prose, not a bullet list. */
function joinList(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1] ?? ""}`;
}

const capitalise = (s: string): string => s.slice(0, 1).toUpperCase() + s.slice(1);

/** "until tomorrow" / "for a week" - The classic's morale and luck grants are timed,
 *  and a bonus with no duration is permanent, which is worth saying. */
function duration(days: number): string {
  if (days <= 0) return "";
  if (days === 1) return " until tomorrow";
  if (days === 7) return " for a week";
  return ` for ${String(days)} days`;
}

/** A visitable that refills is worth a return trip; one that does not is a
 *  once-only. `weeklyReset` and `oneVisitPerHero` are the def's own flags. */
function cadence(def: MapObjectDef): string {
  if (def.weeklyReset) return ", once a week";
  if (def.oneVisitPerHero) return ", once";
  return "";
}

function bonusEffect(def: MapObjectDef): string | null {
  const p = def.payload;
  if (isTrue(p, "obelisk")) return "Reveals a piece of the puzzle map";

  const skill = readString(p, "skill");
  if (skill !== null) return `Teaches ${humanise(skill)}`;

  if (isTrue(p, "manaFull")) return `Restores your spell points${cadence(def)}`;
  const mana = readNumber(p, "mana");
  if (mana > 0) return `Restores ${formatNumber(mana)} spell points`;

  const experience = readNumber(p, "experience");
  if (experience > 0) return `Grants ${formatNumber(experience)} experience${cadence(def)}`;

  const primaries: string[] = [];
  for (const [key, word] of PRIMARY_WORDS) {
    const v = readNumber(p, key);
    if (v > 0) primaries.push(`+${String(v)} ${word}`);
  }
  if (primaries.length > 0) {
    const cost = readNumber(p, "gold");
    const price = cost < 0 ? ` for ${formatNumber(-cost)} gold` : "";
    return `${primaries.join(", ")}${price}${cadence(def)}`;
  }

  const yields: string[] = [];
  for (const res of RESOURCES) {
    const v = readNumber(p, res);
    if (v > 0) yields.push(`${formatNumber(v)} ${resourceWord(res, v)}`);
  }
  if (yields.length > 0) return `Yields ${joinList(yields)}${cadence(def)}`;

  const days = readNumber(p, "days");
  const blessings: string[] = [];
  if (readNumber(p, "morale") > 0) blessings.push("raises morale");
  if (readNumber(p, "luck") > 0) blessings.push("brings good luck");
  if (readNumber(p, "movement") > 0) blessings.push("speeds the hero on");
  if (blessings.length > 0) return capitalise(`${joinList(blessings)}${duration(days)}`);

  // Fourteen defs ship an empty payload because the payload vocabulary has no
  // key for what they do (STATUS.md gap 6). Saying nothing is right: inventing
  // an effect the kernel will not deliver is worse than a bare name.
  return null;
}

const PRIMARY_WORDS: readonly (readonly [string, string])[] = Object.freeze([
  ["attack", "attack"], ["defense", "defence"],
  ["spellPower", "spell power"], ["knowledge", "knowledge"],
]);

/**
 * What visiting this object would do, in one line, or null when there is
 * nothing honest to say. Pure and exported so the test can pin the wording
 * against the shipped content rather than against a paraphrase of it.
 */
export function objectEffect(
  object: ObservedObject, def: MapObjectDef | undefined, content: ContentRegistry | null,
): string | null {
  if (def === undefined) return null;
  const p = def.payload;

  switch (def.kind) {
    case "mine": {
      const res = readString(p, "resource");
      const amount = readNumber(p, "amount");
      return res === null || amount <= 0
        ? null
        : `Produces ${formatNumber(amount)} ${resourceWord(res, amount)} a day`;
    }
    case "dwelling": {
      const creature = readString(p, "creature");
      const cd = creature === null ? undefined : content?.creatures.get(creature as CreatureDefId);
      const growth = readNumber(p, "growth");
      if (cd === undefined) return "Creatures may be recruited here";
      return growth > 0
        ? `Recruit ${cd.namePlural} - ${String(growth)} a week`
        : `Recruit ${cd.namePlural}`;
    }
    case "monster":
      return "Hostile - they will give battle";
    case "bank":
      // The brief's own example. A bank's guard is the whole of what a player
      // needs to know before walking into one; the prize is not scoutable.
      return "Guarded";
    case "resource": {
      const res = readString(p, "resource");
      return res === null ? null : `A pile of ${res}`;
    }
    case "treasure":
      return "Treasure, for the taking";
    case "artifact":
      return "An artifact lies here";
    case "shrine": {
      const spell = readString(p, "spell");
      const sd = spell === null ? undefined : content?.spells.get(spell as SpellDefId);
      return sd === undefined ? "Teaches a spell" : `Teaches ${sd.name}`;
    }
    case "scholar": {
      const primary = readString(p, "primary");
      const amount = readNumber(p, "amount");
      if (primary === null || amount <= 0) return "Teaches the visiting hero";
      const word = PRIMARY_WORDS.find(([k]) => k === primary)?.[1] ?? primary;
      return `+${String(amount)} ${word}`;
    }
    case "bonus":
      return bonusEffect(def);
    case "boat":
      return "Board it to put to sea";
    case "portal":
      return "Steps through to another place";
    case "gate": {
      const colour = readString(p, "colour");
      if (colour === "quest") return "Barred until its quest is done";
      return colour === null ? "Barred" : `Barred - needs the ${colour} key`;
    }
    case "keymaster": {
      const colour = readString(p, "colour");
      return colour === null ? "Grants a key" : `Grants the ${colour} key`;
    }
    case "garrison":
      return "A garrison holds the way";
    case "sign": {
      const text = readString(p, "text");
      return text ?? "A signpost";
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------- the ground

/** What the footing costs, from the terrain def's own `moveCost`. It is the
 *  one thing about bare ground worth a second line, and it is what the move
 *  estimate above it is priced against. */
function footing(kind: TerrainKind, content: ContentRegistry | null): string | null {
  const def = content?.terrain.get(kind);
  if (def === undefined) return null;
  if (def.impassable) return "Impassable";
  if (def.water) return "Open water - a boat is needed";
  if (def.moveCost > 150) return "Heavy going";
  if (def.moveCost > 100) return "Slow going";
  return null;
}

/** The magic ground's name, from the content where it is loaded and from its
 *  id where it is not - the plate must say "Hexed Soil" either way. */
function magicGround(
  obs: PlayerObservation, content: ContentRegistry | null, x: number, y: number, z: number,
): string | null {
  const id = magicGroundAt(obs, x, y, z);
  if (id === null) return null;
  return content?.magicGrounds.get(id)?.name ?? humanise(id);
}

/**
 * Tile coordinates and the level index, for a developer.
 *
 * Kept here so there is exactly one place that formats them, and deliberately
 * called by nothing in the shipping plate: `StatusBar` renders it only when
 * `__HM_DEBUG_COORDS__` is set on `globalThis`, which nothing but a console
 * ever sets. The right long-term home is `__GAME_DEBUG__` in `apps/web`, which
 * this package does not own and must not name (CI greps `dist/` for it).
 */
export const cellDebugText = (x: number, y: number, z: number, levels: number): string =>
  `(${String(x)}, ${String(y)}${levels > 1 ? `, level ${String(z + 1)}` : ""})`;

// ------------------------------------------------------------- hover -> words

/** Pure, so the test can pin the wording without a DOM. */
export function describeHover(
  obs: PlayerObservation, content: ContentRegistry | null, hover: HoverTarget,
): HoverDescription {
  switch (hover.kind) {
    case "NONE":
      // Deliberately empty. The one caller falls back to the ground the
      // selected hero is standing on; naming the screen back at the player is
      // not information, and it is what made two status bars say the same dead
      // word.
      return { title: "", detail: [] };

    case "CELL": {
      const kind = terrainAt(obs, hover.x, hover.y, hover.z);
      if (kind === null) return { title: "Unexplored", detail: [] };
      // The name of the ground, and nothing else. The classic's bar says "Grass".
      // A magic ground is the exception and gets a line of its own: "Magic
      // Plains" under "Grass" is the one thing about a tile a player cannot
      // see from the wash the map paints over it, and it decides how every
      // spell in a fight there behaves.
      const cost = footing(kind, content);
      const magic = magicGround(obs, content, hover.x, hover.y, hover.z);
      return {
        title: humanise(kind),
        detail: [...(magic === null ? [] : [magic]), ...(cost === null ? [] : [cost])],
      };
    }

    case "HERO": {
      const hero = findHero(obs, hover.id);
      if (hero === null) return { title: "Hero", detail: [] };
      const own = obs.self !== null && hero.owner === obs.self.id;
      return {
        title: heroDisplayName(hero, content),
        detail: [
          `Level ${String(hero.level)}`,
          own ? "Your hero" : `Enemy hero (${colourFor(obs, hero.owner as string)})`,
        ],
      };
    }

    case "TOWN": {
      const town = findTown(obs, hover.id);
      if (town === null) return { title: "Town", detail: [] };
      const owner = town.owner === null
        ? "Unclaimed, or out of sight"
        : `Held by ${colourFor(obs, town.owner as string)}`;
      return { title: town.name, detail: [`${humanise(town.faction)} town`, owner] };
    }

    case "OBJECT": {
      const object = findObject(obs, hover.id);
      if (object === null) return { title: "Object", detail: [] };
      const def = content?.mapObjects.get(object.def);
      const effect = objectEffect(object, def, content);
      // Ownership is worth a line only where it changes what the object does
      // for you. A tree has no owner and a chest's owner is nobody.
      const flaggable = object.kind === "mine" || object.kind === "dwelling";
      const owner = object.owner === null
        ? (flaggable ? "Unclaimed" : null)
        : (obs.self !== null && object.owner === obs.self.id
            ? "Yours"
            : `Held by ${colourFor(obs, object.owner as string)}`);
      return {
        title: objectDisplayName(object, def, content),
        detail: [
          ...(effect === null ? [] : [effect]),
          ...(owner === null ? [] : [owner]),
          ...(object.visible ? [] : ["Remembered, not currently in sight"]),
        ],
      };
    }
  }
}

// ------------------------------------------------------------- status line

export interface StatusLine {
  /** The headline. Empty when the pointer is over nothing at all, which is the
   *  signal for the plate to fall back to the selected hero's own ground. */
  readonly text: string;
  /** Supporting lines, already ordered. Never used for the refusal wording. */
  readonly detail: readonly string[];
  /** True when the line is a refusal, which the bar renders on a red plate. */
  readonly illegal: boolean;
  /** The reason code, for the badge. Never used to choose the wording. */
  readonly code: string | null;
}

/**
 * The single line under the map. Priority: a refusal beats a move estimate,
 * which beats a description of whatever is under the cursor. That order is the
 * whole design - the bar exists to answer "why did that not work".
 */
export function statusLine(
  obs: PlayerObservation, content: ContentRegistry | null, status: StatusState,
): StatusLine {
  const described = describeHover(obs, content, status.hover);

  if (status.reason !== null) {
    return {
      text: reasonText(status.reason),
      detail: described.title === "" ? [] : [described.title],
      illegal: true,
      code: status.reason.code,
    };
  }

  if (status.move !== null) {
    const move = status.move;
    if (!move.reachable) {
      return {
        text: `${described.title} - no route`, detail: described.detail,
        illegal: false, code: null,
      };
    }
    const turns = move.turns <= 1 ? "this turn" : `${String(move.turns)} turns`;
    return {
      text: `${described.title} - ${formatNumber(move.costPoints)} movement, ${turns}`,
      detail: described.detail,
      illegal: false,
      code: null,
    };
  }

  return {
    text: described.title, detail: described.detail, illegal: false, code: null,
  };
}
